import { useId, useState } from 'react';
import { ChevronDownIcon, ChevronEndIcon } from '@huaca/ui';
import { FACET_HEADINGS, FACET_ORDER, FACET_VALUE_LABELS } from './labels';
import type { ClosingWindow, FacetCounts, FacetKey } from './types';
import type { FilterPatch, SearchQuery } from './search-state';

/**
 * The filter sidebar.
 *
 * Labels come from the vendored taxonomy rather than from the server: every dimension is a CLOSED
 * vocabulary, and the alternative is a round trip before the filters can paint.
 *
 * A value whose count is zero is rendered DISABLED rather than dropped. Hiding it would make the
 * list change length on every interaction, so the option a user is reaching for moves out from under
 * the pointer — and the counts already arrive in a fixed taxonomy order for the same reason.
 *
 * EVERY DIMENSION IS MULTI-SELECT, and the control is a real `<input type="checkbox">` inside a
 * `<label>` inside a `<fieldset>`. That is not decoration: a pressed-button row leaves "you may pick
 * several here" to be discovered, where a checkbox says it before the first click — and the native
 * control brings its own keyboard behaviour and its own announcement instead of an `aria-pressed`
 * imitation of both.
 *
 * The counts stay meaningful under multi-select because the server already computes each dimension
 * with its OWN selection lifted out: a number answers "how many more would this add", so an
 * unticked value in a filtered dimension reads its real count rather than zero.
 */

/**
 * Which dimensions draw a bar, and which pressure step each of their values takes.
 *
 * ONLY `closing`. It is the one ORDERED dimension here, so bar length encodes a real quantity —
 * how the result set is distributed across time. On a nominal dimension like תחום, length would
 * only restate the number already printed beside it, which is decoration rather than information.
 */
const PRESSURE_STEP: Record<ClosingWindow, string> = {
  now: 'now',
  week: 'near',
  month: 'mid',
  later: 'far',
  none: 'none',
};

const isBarred = (dimension: FacetKey): boolean => dimension === 'closing';

/**
 * The dimensions that start unfolded.
 *
 * ONLY `closing`, and for the same reason it is the only barred one: it is the dimension a reader
 * opens the page already asking about, and the one whose bars say something the counts alone do
 * not. Every other dimension is a list you go looking for.
 *
 * Folding the rest is what makes eight dimensions fit: ActiveFilters.tsx already notes that the
 * open sidebar runs taller than the viewport, so a filter three scrolls down is invisible WHILE it
 * narrows the results — the state where someone concludes the search is broken. Folded, every
 * heading is on screen at once.
 *
 * A folded dimension never hides an active selection: its `נקה (n)` stays in the legend, which is
 * outside the folded part, and every selected value also has a chip above the results.
 */
const INITIALLY_OPEN: readonly FacetKey[] = ['closing'];

export interface FiltersProps {
  query: SearchQuery;
  facets: FacetCounts | null;
  setFilter: (patch: FilterPatch) => void;
}

/**
 * Add or remove one value, SORTED, and collapse an emptied selection to `undefined`.
 *
 * Sorted because the server hashes the selection into the paging cursor and normalises it the same
 * way; an unsorted array would spell the same filter differently depending on click order and get a
 * valid cursor rejected. `undefined` rather than `[]` because the URL must have exactly one spelling
 * for "no filter on this dimension".
 */
function toggled(values: readonly string[], value: string): string[] | undefined {
  const next = values.includes(value) ? values.filter((v) => v !== value) : [...values, value].sort();
  return next.length ? next : undefined;
}

export function Filters({ query, facets, setFilter }: FiltersProps) {
  /* Held here rather than in the URL or in storage: it is a way of looking at the page, not part of
     the query, so a shared link must not carry it and must not arrive folded differently from the
     way the recipient's own sidebar opens. It survives every re-query because this component stays
     mounted while `facets` changes underneath it. */
  const [open, setOpen] = useState<ReadonlySet<FacetKey>>(() => new Set(INITIALLY_OPEN));
  /* `aria-controls` needs an id that is unique in the DOCUMENT, not just in this component. */
  const idPrefix = useId();

  const toggle = (dimension: FacetKey) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(dimension)) next.add(dimension);
      return next;
    });

  return (
    <aside className="tenders-filters" aria-label="סינון">
      {FACET_ORDER.map((dimension) => {
        const rows = facets?.[dimension] ?? [];
        if (rows.length === 0) return null;
        const selected: readonly string[] = query[dimension] ?? [];
        const barred = isBarred(dimension);
        const isOpen = open.has(dimension);
        const listId = `${idPrefix}-${dimension}`;

        /**
         * Ticked values first — EXCEPT on a barred dimension, where the values are a scale and
         * reordering them would destroy the thing the bars are drawn to show. Elsewhere it stops a
         * ticked value sitting below the fold of a long list, where the sidebar would show a filter
         * as unticked while it is actively narrowing the results.
         */
        const ordered = barred
          ? rows
          : [...rows].sort((a, b) => Number(selected.includes(b.value)) - Number(selected.includes(a.value)));

        // Every bar is a share of the largest window, so the lengths are comparable to each other
        // rather than to the total — with one window at 90% of results, per-total bars would all
        // collapse to hairlines.
        const widest = barred ? Math.max(...rows.map((r) => r.count), 1) : 1;

        return (
          <fieldset key={dimension} className={`tenders-facet${isOpen ? '' : ' is-folded'}`}>
            <legend>
              {/*
                A SPAN, and specifically not a div: the row has to be a flex container so the toggle
                can grow to the sidebar's width, and `display` on a `<legend>` itself is the one
                property browsers at this project's Safari floor are entitled to ignore. A span is
                also the only one of the two that is valid here — a legend takes phrasing content.
              */}
              <span className="tenders-facet__head">
                {/*
                  The heading IS the control, so the whole row is the target rather than a chevron
                  beside a label that does nothing. `נקה` stays a sibling: a button inside a button
                  is invalid, and clearing a dimension must not also fold it.
                */}
                <button
                  type="button"
                  className="tenders-facet__toggle"
                  aria-expanded={isOpen}
                  aria-controls={listId}
                  onClick={() => toggle(dimension)}
                >
                  {/* Mirrors itself in RTL — `huaca-icon--dir` on the end chevron, see icons.tsx. */}
                  {isOpen ? (
                    <ChevronDownIcon className="huaca-icon--sm" />
                  ) : (
                    <ChevronEndIcon className="huaca-icon--sm" />
                  )}
                  {FACET_HEADINGS[dimension]}
                </button>
                {selected.length > 0 && (
                  <button
                    type="button"
                    className="tenders-facet__clear"
                    aria-label={`ניקוי הסינון לפי ${FACET_HEADINGS[dimension]}`}
                    onClick={() => setFilter({ [dimension]: undefined } as FilterPatch)}
                  >
                    נקה ({selected.length})
                  </button>
                )}
              </span>
            </legend>
            {/*
              `hidden` rather than an unmounted list: `aria-controls` above must point at an element
              that exists, and a folded dimension keeps its checkboxes out of the tab order and out
              of the accessibility tree the same way either way.
            */}
            <ul id={listId} hidden={!isOpen}>
              {ordered.map(({ value, count }) => {
                const isSelected = selected.includes(value);
                // Zero results and not already ticked ⇒ nothing to select. Disabled rather than
                // hidden, so the row keeps its place in the list.
                const isDisabled = count === 0 && !isSelected;
                const step = barred ? PRESSURE_STEP[value as ClosingWindow] : null;
                const rowClass = [
                  'tenders-facet__value',
                  barred ? 'tenders-facet__value--bar' : '',
                  step ? `tenders-p-${step}` : '',
                  isSelected ? 'is-selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <li key={value}>
                    <label className={rowClass}>
                      <input
                        type="checkbox"
                        className="tenders-facet__box"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => setFilter({ [dimension]: toggled(selected, value) } as FilterPatch)}
                      />
                      <span className="tenders-facet__name">{FACET_VALUE_LABELS[dimension][value] ?? value}</span>
                      {barred && (
                        // The bar sits in its OWN column after the label, so every bar shares a
                        // baseline. Behind the text it would start wherever the label happened to
                        // end, and read as a highlight rather than a measurement.
                        <span className="tenders-facet__track" aria-hidden="true">
                          <span
                            className="tenders-facet__bar"
                            // Floored so a count of 1 is a visible mark rather than a hairline.
                            style={{ inlineSize: count === 0 ? 0 : `${Math.max(8, Math.round((count / widest) * 100))}%` }}
                          />
                        </span>
                      )}
                      <span className="tenders-facet__count">{count}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        );
      })}
    </aside>
  );
}
